import { createSlice } from "@reduxjs/toolkit";

import { Staff } from "@/interfaces/IAuth";
import {
  createShiftPasscodeAsync,
  deleteShiftPasscodeAsync,
  getShiftPasscodesAsync,
  updateShiftPasscodeAsync,
} from "../actions/staff/shift-passcodes";
import {
  createStaffUserAsync,
  deleteStaffUserAsync,
  getStaffUsersAsync,
  getStaffUsersBySearchAsync,
  updateStaffUserAsync,
} from "../actions/staff/staff";
import {
  createStaffDocAsync,
  deleteStaffDocAsync,
  getStaffDocsAsync,
  updateStaffDocAsync,
} from "../actions/staff/staff-documents";

export type ReducerState<T = any> = {
  data: T;
  loading: boolean;
  error: string | null;
  total?: number;
  hasMoreData?: boolean;
};

export type Filter = {
  value: string;
  label: string;
  count: number;
};

interface StaffReducerState {
  // Define your reducer state here
  selectedStaff: string | null;
  staff: ReducerState<Staff[]>;
  documents: ReducerState;
  shiftPasscodes: ReducerState;
  filters: {
    page: number;
    query: string;
    limit: number;
    jobRole: Array<string>;
    status: Array<string>;
  };
}

const initialState: StaffReducerState = {
  // Set initial state here
  selectedStaff: null,
  staff: {
    data: [],
    loading: false,
    error: null,
    hasMoreData: true,
  },
  documents: {
    data: [],
    loading: false,
    error: null,
  },
  shiftPasscodes: {
    data: [],
    loading: false,
    error: null,
    total: 0,
  },
  filters: {
    page: 1,
    query: "",
    limit: 10,
    jobRole: [],
    status: ["active"],
  },
};

export const staffReducer = createSlice({
  name: "staffReducer",
  initialState,
  reducers: {
    // Define staff reducer actions here
    setHasMoreData: (state, action) => {
      // Update state based on the action
      state.staff.hasMoreData = action.payload;
    },
    setSelectedStaff: (state, action) => {
      // Update state based on the action
      state.selectedStaff = action.payload;
    },
    setFilters: (state, action) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
    },
  },
  extraReducers: (builder) => {
    builder
      // staff reducer
      .addCase(getStaffUsersAsync.pending, (state) => {
        state.staff.loading = true;
        state.staff.error = null;
      })
      .addCase(getStaffUsersAsync.fulfilled, (state, action) => {
        state.staff.loading = false;
        const parsedData = JSON.parse(JSON.stringify(action.payload?.data));
        if (parsedData.length === 0) {
          state.staff.hasMoreData = false;
        }

        const notAlreadyPresent = parsedData.filter(
          (item: any) =>
            !state.staff.data.some(
              (existingItem: any) => existingItem._id === item._id
            )
        );

        state.staff.data = [...state.staff.data, ...notAlreadyPresent];
      })
      .addCase(getStaffUsersAsync.rejected, (state, action) => {
        state.staff.loading = false;
        state.staff.error = action.error.message || "Failed to fetch data";
      })
      .addCase(getStaffUsersBySearchAsync.pending, (state) => {
        state.staff.loading = true;
        state.staff.error = null;
      })
      .addCase(getStaffUsersBySearchAsync.fulfilled, (state, action) => {
        state.staff.loading = false;
        const parsedData = JSON.parse(JSON.stringify(action.payload?.data));
        if (parsedData.length === 0) {
          state.staff.hasMoreData = false;
        }
        state.staff.data = action.payload?.data;
      })
      .addCase(getStaffUsersBySearchAsync.rejected, (state, action) => {
        state.staff.loading = false;
        state.staff.error = action.error.message || "Failed to fetch data";
      })

      .addCase(createStaffUserAsync.pending, (state) => {
        state.staff.loading = true;
        state.staff.error = null;
      })
      .addCase(createStaffUserAsync.fulfilled, (state, action) => {
        state.staff.loading = false;
        state.staff.data = [...state.staff.data, action.payload?.data];
      })
      .addCase(createStaffUserAsync.rejected, (state, action) => {
        state.staff.loading = false;
        state.staff.error = action.error.message || "Failed to fetch data";
      })
      .addCase(updateStaffUserAsync.pending, (state) => {
        state.staff.loading = true;
        state.staff.error = null;
      })
      .addCase(updateStaffUserAsync.fulfilled, (state, action) => {
        state.staff.loading = false;
        const { _id } = action.payload?.data;
        const updatedData = state.staff.data.map((item: any) => {
          return item._id === _id ? action.payload?.data : item;
        });
        state.staff.data = updatedData;
      })
      .addCase(updateStaffUserAsync.rejected, (state, action) => {
        state.staff.loading = false;
        state.staff.error = action.error.message || "Failed to fetch data";
      })
      .addCase(deleteStaffUserAsync.pending, (state) => {
        state.staff.loading = true;
        state.staff.error = null;
      })
      .addCase(deleteStaffUserAsync.fulfilled, (state, action) => {
        state.staff.loading = false;
        const { _id } = action.payload?.data;
        const updatedData = state.staff.data.filter(
          (item: any) => item._id !== _id
        );
        state.staff.data = updatedData;
        state.staff.hasMoreData = true;
      })
      .addCase(deleteStaffUserAsync.rejected, (state, action) => {
        state.staff.loading = false;
        state.staff.error = action.error.message || "Failed to fetch data";
      })
      // shift passcodes reducer
      .addCase(getShiftPasscodesAsync.pending, (state) => {
        state.shiftPasscodes.loading = true;
        state.shiftPasscodes.error = null;
      })
      .addCase(getShiftPasscodesAsync.fulfilled, (state, action) => {
        state.shiftPasscodes.loading = false;
        state.shiftPasscodes.data = action.payload?.data;
        state.shiftPasscodes.total = action.payload?.total;
      })
      .addCase(getShiftPasscodesAsync.rejected, (state, action) => {
        state.shiftPasscodes.loading = false;
        state.shiftPasscodes.error =
          action.error.message || "Failed to fetch data";
      })
      .addCase(createShiftPasscodeAsync.pending, (state) => {
        state.shiftPasscodes.loading = true;
        state.shiftPasscodes.error = null;
      })
      .addCase(createShiftPasscodeAsync.fulfilled, (state, action) => {
        state.shiftPasscodes.loading = false;
        state.shiftPasscodes.data = [
          ...state.shiftPasscodes.data,
          action.payload?.data,
        ];
      })
      .addCase(createShiftPasscodeAsync.rejected, (state, action) => {
        state.shiftPasscodes.loading = false;
        state.shiftPasscodes.error =
          action.error.message || "Failed to fetch data";
      })
      .addCase(updateShiftPasscodeAsync.pending, (state) => {
        // state.shiftPasscodes.loading = true;
        state.shiftPasscodes.error = null;
      })
      .addCase(updateShiftPasscodeAsync.fulfilled, (state, action) => {
        // state.shiftPasscodes.loading = false;
        const { _id } = action.payload?.data;
        const updatedData = state.shiftPasscodes.data.map((item: any) =>
          item._id === _id ? action.payload?.data : item
        );
        state.shiftPasscodes.data = updatedData;
      })
      .addCase(updateShiftPasscodeAsync.rejected, (state, action) => {
        // state.shiftPasscodes.loading = false;
        state.shiftPasscodes.error =
          action.error.message || "Failed to fetch data";
      })
      .addCase(deleteShiftPasscodeAsync.pending, (state) => {
        state.shiftPasscodes.error = null;
      })
      .addCase(deleteShiftPasscodeAsync.fulfilled, (state, action) => {
        const { _id } = action.payload?.data;
        const updatedData = state.shiftPasscodes.data.filter(
          (item: any) => item._id !== _id
        );
        state.shiftPasscodes.data = updatedData;
      })
      .addCase(deleteShiftPasscodeAsync.rejected, (state, action) => {
        state.shiftPasscodes.error =
          action.error.message || "Failed to fetch data";
      })
      // documents reducer
      .addCase(getStaffDocsAsync.pending, (state) => {
        state.documents.loading = true;
        state.documents.error = null;
      })
      .addCase(getStaffDocsAsync.fulfilled, (state, action) => {
        state.documents.loading = false;
        state.documents.data = action.payload?.data;
      })
      .addCase(getStaffDocsAsync.rejected, (state, action) => {
        state.documents.loading = false;
        state.documents.error = action.error.message || "Failed to fetch data";
      })
      .addCase(createStaffDocAsync.pending, (state) => {
        // state.documents.loading = true;
        state.documents.error = null;
      })
      .addCase(createStaffDocAsync.fulfilled, (state, action) => {
        // state.documents.loading = false;
        state.documents.data = [...state.documents.data, action.payload?.data];
      })
      .addCase(createStaffDocAsync.rejected, (state, action) => {
        // state.documents.loading = false;
        state.documents.error = action.error.message || "Failed to fetch data";
      })
      .addCase(updateStaffDocAsync.pending, (state) => {
        state.documents.error = null;
      })
      .addCase(updateStaffDocAsync.fulfilled, (state, action) => {
        const { _id } = action.payload?.data;
        const updatedData = state.documents.data.map((item: any) =>
          item._id === _id ? action.payload?.data : item
        );
        state.documents.data = updatedData;
      })
      .addCase(updateStaffDocAsync.rejected, (state, action) => {
        state.documents.error = action.error.message || "Failed to fetch data";
      })
      .addCase(deleteStaffDocAsync.pending, (state) => {
        state.documents.error = null;
      })
      .addCase(deleteStaffDocAsync.fulfilled, (state, action) => {
        const { _id } = action.payload?.data;
        const updatedData = state.documents.data.filter(
          (item: any) => item._id !== _id
        );
        state.documents.data = updatedData;
      })
      .addCase(deleteStaffDocAsync.rejected, (state, action) => {
        state.documents.error = action.error.message || "Failed to fetch data";
      });
  },
});

export const { setSelectedStaff, setHasMoreData, setFilters, resetFilters } =
  staffReducer.actions;
export default staffReducer.reducer;
